import { Button, Flex, Form, Tabs } from 'antd';
import { useCallback, useMemo } from 'react';

import {
  FormSchemaProvider,
  useSchemaFormValidate,
} from '@/components/form/FormSchemaContext';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { SPACE_LG, SPACE_MD } from '@/theme/buildAntdTheme';
import ModalFooter from '@/UI/ModalFooter';
import SectionTitle from '@/UI/SectionTitle/SectionTitle';
import { validateSchemaForm } from '@/utils/validation/validateSchemaForm';
import { hasVisibleAntFormItemError } from '@/utils/validation/visibleFormFieldErrors';

import { evaluateHidden } from './schemaFieldUtils';
import SchemaSectionFields from './SchemaSectionFields';
import { getFormSizeTokens, type FormSizeTokens } from './sizing';

import type {
  FormAction,
  FormFooter,
  SchemaFormProps,
  SchemaSection,
  SectionConfig,
  TabsSectionConfig,
} from './types';
import type React from 'react';

function SchemaSectionBlock({
  section,
  tokens,
  formValues,
}: {
  section: SectionConfig;
  tokens: FormSizeTokens;
  formValues: Record<string, unknown>;
}) {
  if (evaluateHidden(section.hidden, formValues)) {
    return null;
  }

  return (
    <Flex vertical gap={SPACE_MD}>
      {section.title != null && (
        <SectionTitle
          title={section.title}
          description={section.description}
          level={tokens.sectionTitleLevel}
          divider={section.divider || true}
        />
      )}
      <Flex vertical gap={0}>
        <SchemaSectionFields
          section={section}
          tokens={tokens}
          formValues={formValues}
        />
      </Flex>
    </Flex>
  );
}

function SchemaTabsSection({
  section,
  tokens,
  formValues,
}: {
  section: TabsSectionConfig;
  tokens: FormSizeTokens;
  formValues: Record<string, unknown>;
}) {
  if (evaluateHidden(section.hidden, formValues)) {
    return null;
  }

  const tabItems = section.tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    forceRender: true,
    children: (
      <Flex vertical gap={SPACE_MD}>
        {tab.sections.map((tabSection) => (
          <SchemaSectionBlock
            key={tabSection.key}
            section={tabSection}
            tokens={tokens}
            formValues={formValues}
          />
        ))}
      </Flex>
    ),
  }));

  return (
    <Tabs
      activeKey={section.activeKey}
      onChange={section.onChange}
      items={tabItems}
    />
  );
}

function isTabsSection(section: SchemaSection): section is TabsSectionConfig {
  return 'type' in section && section.type === 'tabs';
}

function renderSection(
  section: SchemaSection,
  tokens: FormSizeTokens,
  formValues: Record<string, unknown>,
): React.ReactNode {
  if (isTabsSection(section)) {
    return (
      <SchemaTabsSection
        key={section.key}
        section={section}
        tokens={tokens}
        formValues={formValues}
      />
    );
  }

  return (
    <SchemaSectionBlock
      key={section.key}
      section={section}
      tokens={tokens}
      formValues={formValues}
    />
  );
}

function wrapFooterActionOnClick(
  action: FormAction,
  validateCurrentForm: ReturnType<typeof useSchemaFormValidate>,
): FormAction {
  if (
    action.htmlType === 'submit' ||
    action.onClick == null ||
    action.skipSchemaValidate
  ) {
    return action;
  }

  const originalOnClick = action.onClick;
  return {
    ...action,
    onClick: (event) => {
      if (!validateCurrentForm().ok) {
        return;
      }
      originalOnClick(event);
    },
  };
}

function renderFormActions(
  actions: FormFooter['actions'],
  footer: FormFooter,
  validateCurrentForm: ReturnType<typeof useSchemaFormValidate>,
  hasFormSchema: boolean,
) {
  return (actions ?? []).map((action, index) => {
    const resolvedAction =
      hasFormSchema && action.onClick != null && !action.skipSchemaValidate
        ? wrapFooterActionOnClick(action, validateCurrentForm)
        : action;

    return (
      <Button
        key={resolvedAction.key ?? index}
        type={resolvedAction.type}
        htmlType={resolvedAction.htmlType}
        danger={resolvedAction.danger}
        loading={resolvedAction.loading}
        disabled={resolvedAction.disabled}
        icon={resolvedAction.icon}
        onClick={resolvedAction.onClick}
        size={resolvedAction.size ?? footer.actionSize}
        variant={resolvedAction.variant}
        color={resolvedAction.color}
        data-testid={resolvedAction['data-testid']}
      >
        {resolvedAction.label}
      </Button>
    );
  });
}

function SchemaFormFooter({
  footer,
  validateCurrentForm,
  hasFormSchema,
}: {
  footer: FormFooter;
  validateCurrentForm: ReturnType<typeof useSchemaFormValidate>;
  hasFormSchema: boolean;
}) {
  return (
    <ModalFooter
      danger={renderFormActions(
        footer.dangerActions,
        footer,
        validateCurrentForm,
        hasFormSchema,
      )}
      actions={renderFormActions(
        footer.actions,
        footer,
        validateCurrentForm,
        hasFormSchema,
      )}
    />
  );
}

function SchemaFormInner({
  formInstance,
  formSchema,
  validationScope: validationScopeProp = 'save',
  contractValidationScope,
  size,
  sections,
  children,
  footer,
  layout,
  scrollToFirstError,
  name,
  onFinish,
  onFinishFailed,
  initialValues,
  onValuesChange,
  onFieldsChange,
  validateTrigger,
  disabled,
  className,
  validateOnFinish = true,
  formTestId,
}: Omit<SchemaFormProps, 'form'> & {
  formInstance: NonNullable<SchemaFormProps['form']>;
}) {
  const { showError } = useApiFeedback();
  const validateCurrentForm = useSchemaFormValidate();
  const formValues = (Form.useWatch([], formInstance) ?? {}) as Record<
    string,
    unknown
  >;

  const tokens = useMemo(() => getFormSizeTokens(size ?? 'default'), [size]);

  const handleFinish = useCallback(() => {
    const allValues = formInstance.getFieldsValue(true) as Record<
      string,
      unknown
    >;
    if (formSchema != null && validateOnFinish) {
      const validation = validateSchemaForm(
        formInstance,
        formSchema,
        allValues,
        {
          validationScope: validationScopeProp ?? 'save',
          contractValidationScope,
          formName: name,
        },
      );
      if (!validation.ok) {
        // Field errors are applied + scrolled by validateSchemaForm.
        // Toast only when nothing visible was marked (e.g. form-level `_form`).
        requestAnimationFrame(() => {
          if (!hasVisibleAntFormItemError(name)) {
            showError(validation.firstMessage);
          }
        });
        return;
      }
      onFinish?.({ ...allValues, ...validation.data });
      return;
    }
    onFinish?.(allValues);
  }, [
    formSchema,
    formInstance,
    onFinish,
    showError,
    name,
    validationScopeProp,
    contractValidationScope,
    validateOnFinish,
  ]);

  const content = sections.map((section) =>
    renderSection(section, tokens, formValues),
  );

  return (
    <Form
      form={formInstance}
      layout={layout}
      scrollToFirstError={scrollToFirstError}
      name={name}
      onFinish={handleFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
      onFieldsChange={onFieldsChange}
      validateTrigger={validateTrigger}
      disabled={disabled}
      className={className}
      data-testid={formTestId as string}
    >
      <Flex vertical gap={0}>
        <Flex vertical gap={SPACE_LG}>
          {content}
        </Flex>
        {children}
      </Flex>
      {footer != null ? (
        <SchemaFormFooter
          footer={footer}
          validateCurrentForm={validateCurrentForm}
          hasFormSchema={formSchema != null}
        />
      ) : null}
    </Form>
  );
}

const SchemaForm = ({
  form,
  formSchema,
  validationScope: validationScopeProp = 'save',
  contractValidationScope,
  size = 'default',
  sections,
  children,
  footer,
  layout = 'vertical',
  scrollToFirstError = true,
  name,
  onFinish,
  onFinishFailed,
  initialValues,
  onValuesChange,
  onFieldsChange,
  validateTrigger,
  disabled,
  className,
  validateOnFinish = true,
  formTestId,
}: SchemaFormProps) => {
  const [internalForm] = Form.useForm();
  const formInstance = form ?? internalForm;

  const inner = (
    <SchemaFormInner
      formInstance={formInstance}
      formSchema={formSchema}
      validationScope={validationScopeProp}
      contractValidationScope={contractValidationScope}
      size={size}
      sections={sections}
      footer={footer}
      layout={layout}
      scrollToFirstError={scrollToFirstError}
      name={name}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
      onFieldsChange={onFieldsChange}
      validateTrigger={validateTrigger}
      disabled={disabled}
      className={className}
      validateOnFinish={validateOnFinish}
      formTestId={formTestId}
    >
      {children}
    </SchemaFormInner>
  );

  if (formSchema != null) {
    return (
      <FormSchemaProvider
        formSchema={formSchema}
        validationScope={validationScopeProp}
        contractValidationScope={contractValidationScope}
        form={formInstance}
        formName={name}
      >
        {inner}
      </FormSchemaProvider>
    );
  }

  return inner;
};

export default SchemaForm;
